import { Mutation, Action, VuexModule, getModule, Module } from 'vuex-module-decorators';
import { store } from '../..';
import { GEnum } from '@/constants/enum.defination';
import { StatsCompetencyBoxplot } from '@/models/statistics/stats-competency-boxplot';
import { statsService } from '@/service/api/stats.service';
import { Competency } from '@/models/competency/competency';
import { EChartService } from '@/service/echart/echart.service';
import { CompetencyBoxplotEchartService } from '@/service/echart/competency-boxplot.echart.service';
import { CompetencyScoreCompare, CompetencyScoreRanking } from '@/models/jgrow/competency-score-compare';
import { SurveyAnswer } from '@/models/jgrow/survey-answer';
import { EventSurveyStudentAnswer, EventSurveyCompetencyGrowthTotal } from '@/models/jgrow/event';
import { SurveyActivityAnswer, SurveyCompetencyAnswer, SurveyCompetencySelectActivity } from '@/models/jgrow/survey-answer';

export interface IEventGiftCourseState {
  statsCompetencyBoxplots: StatsCompetencyBoxplot[][]; // コンピテンシー分布
  echartService: EChartService; // echartを制御するサービス
  competencies: Competency[]; // マスタデータ コンピテンシー一覧(コンピテンシー分布のラベル表示に使用)
  eventCompetencies: Competency[]; // イベントで使っているコンピテンシー
  loading: boolean; // チャートデータ取得中
  beforeExamCompetencyBoxplots: StatsCompetencyBoxplot[];
  afterExamCompetencyBoxplots: StatsCompetencyBoxplot[];
  studentAnswers: Map<number, EventSurveyStudentAnswer>;
  studentAnswersAndScores: Map<number, EventSurveyStudentAnswer>;
  competencyGrowthTableSetupData: Map<string, Map<string, EventSurveyCompetencyGrowthTotal>>;
}
@Module({ dynamic: true, store, name: 'eventGiftCourseModule', namespaced: true })
class EventGiftCourseModule extends VuexModule implements IEventGiftCourseState {
  statsCompetencyBoxplots: StatsCompetencyBoxplot[][] = [];
  echartService: EChartService = new CompetencyBoxplotEchartService();
  competencies: Competency[] = [];
  eventCompetencies: Competency[] = [];
  eventCompetencyIDs: number[] = [];
  loading: boolean = false;

  beforeExamCompetencyBoxplots: StatsCompetencyBoxplot[] = [];
  afterExamCompetencyBoxplots: StatsCompetencyBoxplot[] = [];
  studentAnswers: Map<number, EventSurveyStudentAnswer> = new Map<number, EventSurveyStudentAnswer>();
  studentAnswersAndScores: Map<number, EventSurveyStudentAnswer> = new Map<number, EventSurveyStudentAnswer>();
  competencyGrowthTableSetupData: Map<string, Map<string, EventSurveyCompetencyGrowthTotal>> = new Map<string, Map<string, EventSurveyCompetencyGrowthTotal>>();

  get competencyByType(): Competency[] {
    return this.competencies.sort(function (a, b): number {
      return a.competencyType > b.competencyType ? 1 : -1;
    });
  }

  // チャートタイプ別のseriesで使用するデータを取得する
  get chartData(): any {
    return this.statsCompetencyBoxplots
  }

  get hasCompetencyData(): boolean {
    return this.beforeExamCompetencyBoxplots.length > 0 && this.afterExamCompetencyBoxplots.length > 0;
  }

  get eventUseCompetencies(): Competency[] {
    const result: Competency[] = [];
    this.eventCompetencyIDs.forEach((compeID: number) => {
      const found = this.competencies.find((competency: Competency) => {
        if (competency.competencyID === compeID) return competency;
      });
      result.push(found);
    });
    return result;
  }

  get competencyGrowthTableColumns(): boolean {
    return this.beforeExamCompetencyBoxplots.length > 0 && this.afterExamCompetencyBoxplots.length > 0;
  }

  // 他者評価（通常のコンピテンシースコア）の平均スコアまとめ
  get competencyScoreAvgs(): CompetencyScoreRanking {
    let result: CompetencyScoreCompare[] = [];
    // マスタから取得したコンピテンシーを軸に回す
    this.competencies.forEach((compe: Competency) => {
      // 事前受検、事後受検、双方で実施し、結果が存在するコンピテンシーのみを対象とする
      if (
        this.beforeExamCompetencyScoresAvgByCompetencyID.has(compe.competencyID) &&
        this.afterExamCompetencyScoresAvgByCompetencyID.has(compe.competencyID))
      {
        const csc = new CompetencyScoreCompare(compe);
        csc.beforeScore = this.beforeExamCompetencyScoresAvgByCompetencyID.get(compe.competencyID);
        csc.afterScore = this.afterExamCompetencyScoresAvgByCompetencyID.get(compe.competencyID);
        result.push(csc);
      }
    });
    const compares = result.sort((a: CompetencyScoreCompare, b: CompetencyScoreCompare) => {
      return a.scoreDiff < b.scoreDiff ? 1 : -1;
    });
    return new CompetencyScoreRanking(compares);
  }

  // 他者評価（通常のコンピテンシースコア）の平均スコアまとめ
  get competencySelfScoreAvgs(): CompetencyScoreRanking {
    let result: CompetencyScoreCompare[] = [];
    // マスタから取得したコンピテンシーを軸に回す
    this.competencies.forEach((compe: Competency) => {
      // 事前受検、事後受検、双方で実施し、結果が存在するコンピテンシーのみを対象とする
      if (
        this.beforeExamCompetencySelfScoresAvgByCompetencyID.has(compe.competencyID) &&
        this.afterExamCompetencySelfScoresAvgByCompetencyID.has(compe.competencyID))
      {
        const csc = new CompetencyScoreCompare(compe);
        csc.beforeScore = this.beforeExamCompetencySelfScoresAvgByCompetencyID.get(compe.competencyID);
        csc.afterScore = this.afterExamCompetencySelfScoresAvgByCompetencyID.get(compe.competencyID);
        result.push(csc);
      }
    });
    const compares = result.sort((a: CompetencyScoreCompare, b: CompetencyScoreCompare) => {
      return a.scoreDiff < b.scoreDiff ? 1 : -1;
    });
    return new CompetencyScoreRanking(compares);
  }

  // 事前受検 - 1 他者評価スコア 全生徒たちのスコアをコンピテンシーIDごとにまとめる
  get beforeExamCompetencyScoresByCompetencyID(): Map<number, number[]> {
    let result: Map<number, number[]> = new Map();
    this.beforeExamCompetencyBoxplots.forEach((compe: StatsCompetencyBoxplot) => {
      compe.competencyScores.forEach((score: number, cid: number) => {
        if (!result.has(cid)) {
          result.set(cid, []);
        }
        if (score > 0) result.get(cid).push(score);
      });
    });
    return result;
  }

  // 事前受検 - 2 事前受検の生徒たちのスコアから {competencyID => avg} というmapを作成
  get beforeExamCompetencyScoresAvgByCompetencyID(): Map<number, number> {
    let result: Map<number, number> = new Map<number, number>();
    this.beforeExamCompetencyScoresByCompetencyID.forEach((scores: number[], cid: number) => {
      if (scores.length) {
        let avg = Math.floor((scores.reduce((sum, num) => sum + num, 0) / scores.length) * 100);
        result.set(cid, avg);
      }
    });
    return result;
  }

  // 事前受検(self) - 1 自己評価スコア 全生徒たちのスコアをコンピテンシーIDごとにまとめる
  get beforeExamCompetencySelfScoresByCompetencyID(): Map<number, number[]> {
    let result: Map<number, number[]> = new Map();
    this.beforeExamCompetencyBoxplots.forEach((compe: StatsCompetencyBoxplot) => {
      compe.selfEvalScores.forEach((score: number, cid: number) => {
        if (!result.has(cid)) {
          result.set(cid, []);
        }
        if (score > 0) result.get(cid).push(score);
      });
    });
    return result;
  }

  // 事前受検(self) - 2 事前受検の生徒たちの自己評価スコアから {competencyID => avg} というmapを作成
  get beforeExamCompetencySelfScoresAvgByCompetencyID(): Map<number, number> {
    let result: Map<number, number> = new Map();
    this.beforeExamCompetencySelfScoresByCompetencyID.forEach((scores: number[], cid: number) => {
      if (scores.length) {
        let avg = Math.floor(scores.reduce((sum, num) => sum + num, 0) / scores.length);
        result.set(cid, avg)
      }
    });
    return result;
  }

  // 事後受検 - 1 事後受検の生徒たちのスコアから {competencyID => avg} というmapを作成
  get afterExamCompetencyScoresByCompetencyID(): Map<number, number[]> {
    let result: Map<number, number[]> = new Map();
    this.afterExamCompetencyBoxplots.forEach((compe: StatsCompetencyBoxplot) => {
      compe.competencyScores.forEach((score: number, cid: number) => {
        if (!result.has(cid)) {
          result.set(cid, []);
        }
        if (score > 0) result.get(cid).push(score);
      });
    });
    return result;
  }

  // 事後受検 - 2 事後受検の生徒たちのスコアから {competencyID => avg} というmapを作成
  get afterExamCompetencyScoresAvgByCompetencyID(): Map<number, number> {
    let result: Map<number, number> = new Map();
    this.afterExamCompetencyScoresByCompetencyID.forEach((scores: number[], cid: number) => {
      if (scores.length) {
        let avg = Math.floor((scores.reduce((sum, num) => sum + num, 0) / scores.length) * 100);
        result.set(cid, avg)
      }
    });
    return result;
  }

  // 事後受検(self) - 1 事後受検の生徒たちのスコアから {competencyID => avg} というmapを作成
  get afterExamCompetencySelfScoresByCompetencyID(): Map<number, number[]> {
    let result: Map<number, number[]> = new Map();
    this.afterExamCompetencyBoxplots.forEach((compe: StatsCompetencyBoxplot) => {
      compe.selfEvalScores.forEach((score: number, cid: number) => {
        if (!result.has(cid)) {
          result.set(cid, []);
        }
        if (score > 0) result.get(cid).push(score);
      });
    });
    return result;
  }

  // 事後受検(self) - 2 事後受検の生徒たちのスコアから {competencyID => avg} というmapを作成
  get afterExamCompetencySelfScoresAvgByCompetencyID(): Map<number, number> {
    let result: Map<number, number> = new Map();
    this.afterExamCompetencySelfScoresByCompetencyID.forEach((scores: number[], cid: number) => {
      if (scores.length) {
        let avg = Math.floor(scores.reduce((sum, num) => sum + num, 0) / scores.length);
        result.set(cid, avg)
      }
    });
    return result;
  }

  @Mutation
  setLoading(value: boolean) {
    this.loading = value;
  }

  @Mutation
  setCompetencies(value: Competency[]) {
    this.competencies = value;
  }

  @Mutation
  setEventCompetencies(value: number[]) {
    const sorted = [...value].sort((a, b): number => {
      return a > b ? 1 : -1;
    });
    this.eventCompetencyIDs = sorted;
  }

  @Mutation
  setEChartService(echartService: EChartService) {
    this.echartService = echartService;
  }

  @Mutation
  addStatsCompetencyBoxplots(value: StatsCompetencyBoxplot[]) {
    this.statsCompetencyBoxplots.push(value);
  }

  @Mutation
  setBeforeStatsCompetencyBoxplots(value: StatsCompetencyBoxplot[]) {
    this.beforeExamCompetencyBoxplots = value;
  }

  @Mutation
  setAfterStatsCompetencyBoxplots(value: StatsCompetencyBoxplot[]) {
    this.afterExamCompetencyBoxplots = value;
  }

  @Mutation
  setSurveyAnswers(values: SurveyAnswer[]) {
    const tmpMap = new Map<number, EventSurveyStudentAnswer>();
    // 事前（type=1） 事後（type=2） の SurveyAnswer が回る
    const answersByStudentID = values.forEach((ans: SurveyAnswer) => {
      // 事前だったら
      if (ans.type === GEnum.SurveysType.Before) {
        // 事前ではアクティビティの期待度に対する回答しかない
        ans.activityAnswers.forEach((saa: SurveyActivityAnswer) => {
          // 保持していなければ新たに保持する
          if (!tmpMap.has(saa.userID)) tmpMap.set(saa.userID, new EventSurveyStudentAnswer(saa.userID));
          tmpMap.get(saa.userID).beforeActivityAnswers.push(saa);
        });
      }
      // 事後だったら
      if (ans.type === GEnum.SurveysType.After) {
        // 事後：アクティビティの参加度に対する回答
        ans.activityAnswers.forEach((saa: SurveyActivityAnswer) => {
          // 保持していなければ新たに保持する
          if (!tmpMap.has(saa.userID)) tmpMap.set(saa.userID, new EventSurveyStudentAnswer(saa.userID));
          tmpMap.get(saa.userID).afterActivityAnswers.push(saa);
        });
        // 事後：コンピテンシーに対する回答
        ans.competencyAnswers.forEach((sca: SurveyCompetencyAnswer) => {
          // 保持していなければ新たに保持する
          if (!tmpMap.has(sca.userID)) tmpMap.set(sca.userID, new EventSurveyStudentAnswer(sca.userID));
          tmpMap.get(sca.userID).afterCompetencyAnswers.push(sca);
        });
        // 事後：コンピテンシーとアクティビティの関連回答
        ans.selectActivities.forEach((scs: SurveyCompetencySelectActivity) => {
          // 保持していなければ新たに保持する
          if (!tmpMap.has(scs.userID)) tmpMap.set(scs.userID, new EventSurveyStudentAnswer(scs.userID));
          tmpMap.get(scs.userID).afterSelectActivities.push(scs);
        });
      }
    });
    this.studentAnswers = tmpMap;
  }

  @Mutation
  setStudentAnswersAndScores() {
    this.beforeExamCompetencyBoxplots.forEach((beforeCompeScores: StatsCompetencyBoxplot) => {
      // アンケート結果が存在する生徒のみ対象 アンケート結果は存在しないが、受検は完了しているパターンは除く
      if (this.studentAnswers.has(beforeCompeScores.student.userID)) {
        this.studentAnswers.get(beforeCompeScores.student.userID).beforeExamCompetencyScores = beforeCompeScores;
      }
    });
    this.afterExamCompetencyBoxplots.forEach((afterCompeScores: StatsCompetencyBoxplot) => {
      // アンケート結果が存在する生徒のみ対象 アンケート結果は存在しないが、受検は完了しているパターンは除く
      if (this.studentAnswers.has(afterCompeScores.student.userID)) {
        this.studentAnswers.get(afterCompeScores.student.userID).afterExamCompetencyScores = afterCompeScores;
      }
    });
    this.studentAnswersAndScores = this.studentAnswers;
  }

  @Mutation
  setCompetencyGrowthTableSetupData() {
    let tmpData = new Map<string, Map<string, EventSurveyCompetencyGrowthTotal>>();
    this.eventCompetencyIDs.forEach((rowCompetencyID: number) => {
      const rowID = "competency_" + rowCompetencyID;
      if (!tmpData.has(rowID)) {
        tmpData.set(rowID, new Map<string, EventSurveyCompetencyGrowthTotal>())
      }
      this.eventCompetencyIDs.forEach((colCompetencyID: number) => {
        const colID = "competency_" + colCompetencyID;
        if (!tmpData.get(rowID).has(colID)) {
          tmpData.get(rowID).set(colID, new EventSurveyCompetencyGrowthTotal(rowCompetencyID, colCompetencyID))
        }
        this.studentAnswersAndScores.forEach((student: EventSurveyStudentAnswer) => {
          // この生徒は「rowCompetency = 横方向」が成長しているか
          if (student.isCompetencyGrowth(rowCompetencyID)) {
            // コンピテンシーが成長した生徒をカウントアップ
            tmpData.get(rowID).get(colID).growthCount++;
            // 「rowCompetency = 横方向」が成長した生徒が、「colCompetency = 縦方向」についてポジティブ回答したか
            if (student.isCompetencyPositive(colCompetencyID)) {
              tmpData.get(rowID).get(colID).positiveCount++;
            }
          }
        });
      });
    });
    this.competencyGrowthTableSetupData = tmpData;
  }

  @Mutation
  clean() {
    this.statsCompetencyBoxplots = [];
    this.competencies = [];
    this.eventCompetencies = [];
    this.eventCompetencyIDs = [];
    this.beforeExamCompetencyBoxplots = [];
    this.afterExamCompetencyBoxplots = [];
    this.studentAnswers = new Map<number, EventSurveyStudentAnswer>();
    this.studentAnswersAndScores = new Map<number, EventSurveyStudentAnswer>();
    this.competencyGrowthTableSetupData = new Map<string, Map<string, EventSurveyCompetencyGrowthTotal>>();
  }

  /**
   * コンピテンシー分布取得
   * @param request 統計情報の検索条件パラメータ
   */
  @Action({ rawError: true })
  async fetchCompetencyDistribution(ids: number[]) {
    if (ids[0] > 0) {
      const result: StatsCompetencyBoxplot[] = await statsService.getCompetencyDistribution(0, 0, '', ids[0]);
      //this.addStatsCompetencyBoxplots(result);
      this.setBeforeStatsCompetencyBoxplots(result);
    }
    if (ids[1] > 0) {
      const result: StatsCompetencyBoxplot[] = await statsService.getCompetencyDistribution(0, 0, '', ids[1]);
      //this.addStatsCompetencyBoxplots(result);
      this.setAfterStatsCompetencyBoxplots(result);
    }
  }
}

export const eventGiftCourseModule = getModule(EventGiftCourseModule);
